// ====================================================== //
// NewsBanner.js                                          //
//  • Fetches all news and renders horizontal scroll      //
// ====================================================== //
import React, { useEffect, useRef, useState } from "react";
import "./NewsBanner.css";
import NewsCardSmall from "./NewsCardSmall";
import { API_BASE } from "../config";

const NewsBanner = () => {
  const [news, setNews] = useState([]);
  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const bannerRef = useRef(null);
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check if content overflows and arrows are needed
  const checkOverflow = () => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      setHasOverflow(scrollWidth > clientWidth);
    }
  };

  /* ---- resize listener for mobile toggle ------------------------- */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Check overflow on resize
      setTimeout(checkOverflow, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ---- fetch all news on mount ----------------------------------- */
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/news`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        setNews(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  /* ---- Check overflow when news data changes -------------------- */
  useEffect(() => {
    if (news.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(checkOverflow, 100);
    }
  }, [news]);

  /* ---- trigger entry animation once ------------------------------ */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          obs.disconnect();
        }
      },
      { root: null, threshold: 0, rootMargin: "0px 0px -25% 0px" }
    );
    if (bannerRef.current) obs.observe(bannerRef.current);
    return () => obs.disconnect();
  }, []);

  /* ---- Keyboard navigation for accessibility -------------------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!scrollRef.current || !hasOverflow) return;

      const scrollAmount = 350; // Width of one card + gap

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    };

    // Only add listener when the banner is focused or contains focus
    const banner = bannerRef.current;
    if (banner) {
      banner.addEventListener('keydown', handleKeyDown);
      return () => banner.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasOverflow]);



  // Helper function to get scroll amount based on screen size
  const getScrollAmount = () => {
    let cardWidth, gap;

    if (window.innerWidth <= 480) {
      cardWidth = 280;
      gap = 20;
    } else if (window.innerWidth <= 768) {
      cardWidth = 300;
      gap = 20;
    } else {
      cardWidth = 400;
      gap = 24;
    }

    return cardWidth + gap;
  };

  const scrollLeft = () => {
    if (scrollRef.current && hasOverflow) {
      const cardWidth = getScrollAmount();
      scrollRef.current.scrollBy({
        left: -cardWidth,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current && hasOverflow) {
      const cardWidth = getScrollAmount();
      scrollRef.current.scrollBy({
        left: cardWidth,
        behavior: 'smooth'
      });
    }
  };

  if (loading || news.length === 0) return null; // show nothing until ready

  return (
    <section
      className="news-banner"
      ref={bannerRef}
      tabIndex="0"
      role="region"
      aria-label="Latest news articles"
    >
      <p className="header-title">News</p>

      <div className="news-scroll-container">
        {hasOverflow && (
          <>
            <button className="news-nav-btn prev" onClick={scrollLeft} aria-label="Scroll to previous news">
              ‹ {/* Left arrow */}
            </button>
            <button className="news-nav-btn next" onClick={scrollRight} aria-label="Scroll to next news">
              › {/* Right arrow */}
            </button>
          </>
        )}
        <div
          className={`news-scroll-wrapper ${animate ? "animate" : ""}`}
          ref={scrollRef}
          role="list"
          aria-label="Scroll horizontally to view more articles"
        >
          <div className="news-slides">
            {news.map((item) => (
              <div key={item.news_id || item.id} className="news-slide" role="listitem">
                <NewsCardSmall item={item} variant="banner" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsBanner;
